import { MediaManagerAssetLibraryDto } from '../../../models/image/dto/media-manager-asset-library-dto';
import { WebSocketMessageContextCommand } from '../../../models/web-sockets/contexts/web-socket-message-context-command';
import { environment } from '../../../../environments/environment';
import { findAssetInLibrary, findFolderInLibrary } from '../mutations/asset-library-lookups';
import {
  MediaManagerFileSystem,
  MediaManagerFolderTreeNode
} from 'src/app/models/media-manager/media-manager-file-system';

/** Feature flags controlling which filesystem-debug side effects fire. Flip flags on when triaging drift. */
export interface MediaManagerDebugOptions {
  logFilesystemRebuilds: boolean;
  logFullViewTreeRegenerations: boolean;
  logSelectedSubFolderViewRegenerations: boolean;
  printFileSystemTree: boolean;
  printEntireFolderTreeView: boolean;
  printSelectedSubFolderTreeView: boolean;
}

/**
 * Default debug options for the Media Manager. Only the selected-subtree logs are enabled to keep the console
 * mostly quiet in development; everything else is opt-in via a code change here when investigating a specific
 * drift or projection bug.
 */
export const defaultMediaManagerDebugOptions: MediaManagerDebugOptions = {
  logFilesystemRebuilds: false,
  logFullViewTreeRegenerations: false,
  logSelectedSubFolderViewRegenerations: true,
  printFileSystemTree: false,
  printEntireFolderTreeView: false,
  printSelectedSubFolderTreeView: false
};

/**
 * All Media Manager debug logging routes through this class so the "should I even log?" gate lives in exactly
 * one place. Instantiate once per domain model / state store and share it across the collaborators that need
 * to emit diagnostics.
 */
export class MediaManagerDebugLogger {
  constructor(public readonly options: MediaManagerDebugOptions = defaultMediaManagerDebugOptions) {}

  /** Debug output is skipped in production builds. */
  shouldPrint(): boolean {
    return !environment.production;
  }

  /** Print a summary line and/or the full filesystem tree when a rebuild happens. */
  logFilesystemRebuilt(filesystem: MediaManagerFileSystem): void {
    if (!this.shouldPrint()) return;
    if (this.options.printFileSystemTree) {
      filesystem.debugPrintFileSystemTree();
    }
    if (this.options.logFilesystemRebuilds) {
      console.info(`MediaManager filesystem rebuilt from asset library.`);
    }
  }

  /**
   * Print a summary line and/or the entire folder-tree projection when it regenerates. Scaffolded for future
   * use; the current MediaManagerFileSystem does not always call this path.
   */
  logFullFolderTreeRegenerated(
    filesystem: MediaManagerFileSystem,
    folderTree: MediaManagerFolderTreeNode,
    reason?: string,
    filesystemVersion?: number
  ): void {
    if (!this.shouldPrint()) return;
    if (this.options.printEntireFolderTreeView) {
      filesystem.debugPrintFullFolderTreeView(folderTree);
    }
    if (this.options.logFullViewTreeRegenerations) {
      console.info(
        `MediaManager full view folder tree regenerated${this.formatFilesystemChange(filesystemVersion, reason)}.`
      );
    }
  }

  /** Same as {@link logFullFolderTreeRegenerated} but for a projected subtree rooted at a specific folder. */
  logSelectedSubFolderTreeRegenerated(
    filesystem: MediaManagerFileSystem,
    selectedSubFolderId: string,
    subFolderTree: MediaManagerFolderTreeNode | null,
    reason?: string,
    filesystemVersion?: number
  ): void {
    if (!this.shouldPrint()) return;
    if (this.options.printSelectedSubFolderTreeView) {
      filesystem.debugPrintFolderSubTreeView(selectedSubFolderId, subFolderTree);
    }
    if (this.options.logSelectedSubFolderViewRegenerations) {
      console.info(
        `MediaManager selected subtree folder view regenerated` +
          ` for '${selectedSubFolderId}'${this.formatFilesystemChange(filesystemVersion, reason)}.`
      );
    }
  }

  /**
   * Warn when a `ReloadAssetLibrary` websocket command forces a full asset library refetch and filesystem
   * rebuild. Loud enough to notice, but only in development.
   */
  warnFilesystemForceRebuiltFromReloadAssetLibraryCommand(): void {
    if (!this.shouldPrint()) return;
    console.warn(
      `MediaManager filesystem forcefully rebuilt due to websocket` +
        ` ${WebSocketMessageContextCommand.ReloadAssetLibrary} command.`
    );
  }

  /** Note that a remote asset-delete reconciliation was skipped because the same asset is being deleted locally. */
  logRemoteAssetDeleteSkippedForLocalDelete(assetName: string): void {
    if (!this.shouldPrint()) return;
    console.info(
      `MediaManager skipped remote asset delete reconciliation` +
        ` for '${assetName}' because it belongs to a local delete batch.`
    );
  }

  /** Note that a remote folder-delete reconciliation was skipped because the same folder is being deleted locally. */
  logRemoteFolderDeleteSkippedForLocalDelete(folderName: string): void {
    if (!this.shouldPrint()) return;
    console.info(
      `MediaManager skipped remote folder delete reconciliation` +
        ` for '${folderName}' because it belongs to a local delete.`
    );
  }

  /** Note that a bulk-delete could not be patched incrementally and is falling back to a full refetch. */
  logBulkDeletePatchFallback(): void {
    if (!this.shouldPrint()) return;
    console.info('MediaManager could not patch an accepted bulk delete. Fetching the AssetLibrary instead.');
  }

  /** Note that a bulk-delete WebSocket job finished, using the response's built-in summary. */
  logBulkDeleteFinished(summaryForLog: string | null | undefined): void {
    if (!this.shouldPrint()) return;
    console.info(`MediaManager bulk delete finished: ${summaryForLog}`);
  }

  /** Best-effort asset display name for a log line. Falls back to the unique id when the library is unknown. */
  getAssetNameForDebugLog(assetUniqueId: string, assetLibrary: MediaManagerAssetLibraryDto | null): string {
    const asset = assetLibrary ? findAssetInLibrary(assetUniqueId, assetLibrary) : null;
    return asset?.displayName?.trim() || asset?.fileName?.trim() || assetUniqueId;
  }

  /** Best-effort folder display name for a log line. Falls back to the folder id when the library is unknown. */
  getFolderNameForDebugLog(folderId: string, assetLibrary: MediaManagerAssetLibraryDto | null): string {
    const folder = assetLibrary ? findFolderInLibrary(folderId, assetLibrary) : null;
    return folder?.name?.trim() || folderId;
  }

  private formatFilesystemChange(filesystemVersion?: number, reason?: string): string {
    const versionMessage = filesystemVersion === undefined ? '' : `, filesystem version -> ${filesystemVersion}`;
    const reasonMessage = reason?.trim() ? `, reason -> ${reason.trim()}` : '';
    return `${versionMessage}${reasonMessage}`;
  }
}
